import useVuelidate from '@vuelidate/core'
import type { GlobalConfig, ValidationArgs } from '@vuelidate/core'
import { useErrors } from '~/composables/use-errors'
import { ErrorApi } from '~/shared/api'
import type { Form, FormErrors } from '~/shared/api'
import { messages } from '~/shared/const/messages'
import { useNotificationsStore } from '~/store/notifications'

export function useForm<
  TRules extends ValidationArgs,
  TForm extends Form<object> & { [K in keyof TRules]: unknown },
>(create: () => TForm, rules: TRules, config: GlobalConfig = {}) {
  const form = reactive(create()) as TForm
  const serverErrors = ref<FormErrors>({})
  const v$ = useVuelidate(rules, form, {
    $autoDirty: true,
    ...config,
    $externalResults: serverErrors,
  })
  const isSubmitting = ref(false)
  const notifications = useNotificationsStore()
  const { loggedError } = useErrors()

  async function submit<T>(request: () => Promise<T>): Promise<T | null> {
    if (isSubmitting.value) return null

    v$.value.$clearExternalResults()

    if (!(await v$.value.$validate())) return null

    isSubmitting.value = true

    try {
      return await request()
    } catch (error) {
      if (ErrorApi.isApiError(error) && Object.keys(error.validation).length) {
        showServerErrors(form.mapServerErrors(error.validation))
      } else {
        notifications.pushError(messages.errors.request)
        if (error instanceof Error) loggedError(error)
      }

      return null
    } finally {
      isSubmitting.value = false
    }
  }

  function showServerErrors(errors: FormErrors) {
    const fieldErrors: FormErrors = {}
    const otherErrors: string[] = []

    for (const [field, fieldMessages] of Object.entries(errors)) {
      if (field in rules) {
        fieldErrors[field] = fieldMessages
      } else {
        otherErrors.push(...fieldMessages)
      }
    }

    serverErrors.value = fieldErrors

    if (otherErrors.length) notifications.pushError(otherErrors.join('. '))
  }

  function reset() {
    Object.assign(form, create())
    v$.value.$clearExternalResults()
    v$.value.$reset()
  }

  return { form, v$, submit, reset, isSubmitting }
}
